import { Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../localization/useDateTime"

import { formatTimeWindow } from "../meetingRoomsLib"
import { MeetingRoomFreeLineKind } from "../meetingRoomsTypes"
import type { MeetingRoomBusyInterval, MeetingRoomTableRow } from "../meetingRoomsTypes"

const MAX_LATER_WINDOWS = 3

const useStyles = makeStyles()((theme) => ({
    time: {
        color: theme.palette.tailwind.slate[700],
    },
    line: {
        fontWeight: 500,
        color: theme.palette.tailwind.slate[700],
    },
    freeLine: {
        color: theme.palette.tailwind.emerald[700],
    },
    bookedLine: {
        fontWeight: 700,
        color: theme.palette.tailwind.red[700],
    },
    laterLine: {
        fontWeight: 600,
        color: theme.palette.tailwind.slate[500],
    },
}))

type Props = Readonly<{
    row: MeetingRoomTableRow
    dayEndMillis: number
}>

export function MeetingRoomTimeCell({ row, dayEndMillis }: Props) {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()
    const { booking, freeLine, laterWindows, room } = row

    const formatTime = (millis: number) => dateTimeFactory.formatTime(dateTimeFactory.fromMillis(millis))
    const formatLaterWindow = (timeRange: MeetingRoomBusyInterval) =>
        timeRange.endMillis >= dayEndMillis
            ? `${formatTime(timeRange.startMillis)}→`
            : formatTimeWindow(dateTimeFactory, timeRange)

    const bookingStart = dateTimeFactory.fromApiOrUndefined(booking?.startDateTime)
    const bookingEnd = dateTimeFactory.fromApiOrUndefined(booking?.endDateTime)
    const shownLaterWindows = laterWindows.slice(0, MAX_LATER_WINDOWS).map(formatLaterWindow).join(", ")
    const hiddenLaterWindows = Math.max(laterWindows.length - MAX_LATER_WINDOWS, 0)

    const renderFreeLine = () => {
        switch (freeLine.kind) {
            case MeetingRoomFreeLineKind.OUT_OF_SERVICE:
                return (
                    <Typography variant="caption" className={classes.line}>
                        {room.outOfServiceMessage}
                    </Typography>
                )
            case MeetingRoomFreeLineKind.FREE_FROM:
                return (
                    <LocalizedStrict
                        id="service-center-meeting-rooms-table-free-from"
                        vars={{ time: freeLine.timeRange ? formatTime(freeLine.timeRange.startMillis) : "" }}
                    >
                        <Typography variant="caption" className={classes.line}>
                            Available from
                        </Typography>
                    </LocalizedStrict>
                )
            case MeetingRoomFreeLineKind.FREE_BETWEEN:
                return (
                    <LocalizedStrict
                        id="service-center-meeting-rooms-table-free-between"
                        vars={{
                            timeRange: freeLine.timeRange ? formatTimeWindow(dateTimeFactory, freeLine.timeRange) : "",
                        }}
                    >
                        <Typography variant="caption" className={classes.line}>
                            Available
                        </Typography>
                    </LocalizedStrict>
                )
            case MeetingRoomFreeLineKind.FREE_UNTIL:
                return (
                    <LocalizedStrict
                        id="service-center-meeting-rooms-table-free-until"
                        vars={{ time: freeLine.timeRange ? formatTime(freeLine.timeRange.endMillis) : "" }}
                    >
                        <Typography variant="caption" className={cx(classes.line, classes.freeLine)}>
                            Available until
                        </Typography>
                    </LocalizedStrict>
                )
            case MeetingRoomFreeLineKind.FREE_REST_OF_DAY:
                return (
                    <LocalizedStrict id="service-center-meeting-rooms-table-free-rest-of-day">
                        <Typography variant="caption" className={cx(classes.line, classes.freeLine)}>
                            Available for the rest of the day
                        </Typography>
                    </LocalizedStrict>
                )
            case MeetingRoomFreeLineKind.BOOKED_REST_OF_DAY:
                return (
                    <LocalizedStrict id="service-center-meeting-rooms-table-booked-rest-of-day">
                        <Typography variant="caption" className={classes.bookedLine}>
                            Booked for the rest of the day
                        </Typography>
                    </LocalizedStrict>
                )
            case MeetingRoomFreeLineKind.NO_FREE_TIME:
                return (
                    <LocalizedStrict id="service-center-meeting-rooms-table-no-free-time-today">
                        <Typography variant="caption" className={classes.bookedLine}>
                            No available time today
                        </Typography>
                    </LocalizedStrict>
                )
        }
    }

    return (
        <Stack spacing=".125rem">
            {bookingStart && bookingEnd && (
                <Typography variant="subtitle2" className={classes.time}>
                    {formatTimeWindow(dateTimeFactory, {
                        startMillis: bookingStart.toMillis(),
                        endMillis: bookingEnd.toMillis(),
                    })}
                </Typography>
            )}
            {renderFreeLine()}
            {laterWindows.length > 0 && (
                <LocalizedStrict
                    id="service-center-meeting-rooms-table-free-later"
                    vars={{ windows: shownLaterWindows, hidden: hiddenLaterWindows }}
                >
                    <Typography variant="caption" className={classes.laterLine}>
                        then {shownLaterWindows}
                    </Typography>
                </LocalizedStrict>
            )}
        </Stack>
    )
}
